import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Coins, ArrowUpRight, ArrowDownLeft, TrendingUp, TrendingDown, ShieldCheck, Clock, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, FullLoader, ProviderNotice } from "../components/common";
import { PortfolioChart } from "../components/PortfolioChart";

const COIN = {
  BTC: { name: "Bitcoin", color: "#F7931A" },
  ETH: { name: "Ethereum", color: "#627EEA" },
  USDC: { name: "USD Coin", color: "#2775CA" },
  USDT: { name: "Tether", color: "#26A17B" },
  SOL: { name: "Solana", color: "#14F195" },
  MATIC: { name: "Polygon", color: "#8247E5" },
  POL: { name: "Polygon", color: "#8247E5" },
  BNB: { name: "BNB", color: "#F3BA2F" },
  XRP: { name: "XRP", color: "#23292F" },
  ADA: { name: "Cardano", color: "#0033AD" },
  DOGE: { name: "Dogecoin", color: "#C2A633" },
  AVAX: { name: "Avalanche", color: "#E84142" },
  LTC: { name: "Litecoin", color: "#345D9D" },
  DOT: { name: "Polkadot", color: "#E6007A" },
  DAI: { name: "Dai", color: "#F5AC37" },
  LINK: { name: "Chainlink", color: "#2A5ADA" },
  TRX: { name: "TRON", color: "#EF0027" },
  BCH: { name: "Bitcoin Cash", color: "#8DC351" },
};

const ICON_BASE = "https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/svg/color";

function CoinIcon({ asset, size = 40 }) {
  const [err, setErr] = useState(false);
  const meta = COIN[asset] || {};
  if (err || !asset) {
    return (
      <span className="grid place-items-center rounded-full font-bold text-xs text-white shrink-0"
        style={{ width: size, height: size, background: meta.color || "#4B23CC" }}>
        {asset}
      </span>
    );
  }
  return (
    <img src={`${ICON_BASE}/${asset.toLowerCase()}.svg`} alt={asset} onError={() => setErr(true)}
      className="rounded-full shrink-0 bg-white" style={{ width: size, height: size }} />
  );
}

export default function Crypto() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [onramp, setOnramp] = useState(null);
  const [markets, setMarkets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [widgetUrl, setWidgetUrl] = useState(null);
  const [pendingNotice, setPendingNotice] = useState(false);

  const loadPrices = async () => {
    try {
      const [res, mk] = await Promise.all([
        api.get("/crypto/status"),
        api.get("/crypto/markets"),
      ]);
      setData(res.data); setMarkets(mk.data);
    } catch (e) { /* keep last prices on transient failure */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [res, cfg, mk] = await Promise.all([
          api.get("/crypto/status"),
          api.get("/crypto/onramp/config"),
          api.get("/crypto/markets"),
        ]);
        setData(res.data); setOnramp(cfg.data); setMarkets(mk.data);
      } catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
    const id = setInterval(loadPrices, 45000); // live prices refresh
    return () => clearInterval(id);
  }, []);

  const openWidget = async (product, asset) => {
    if (!onramp?.ready) {
      toast.error("Crypto buy/sell isn't connected yet. Please check back soon.");
      return;
    }
    if (onramp.kyc_required && !onramp.kyc_verified) {
      toast.error("Verify your identity (KYC) first to buy or sell crypto.");
      nav("/app/kyc");
      return;
    }
    if (launching) return;
    setLaunching(true);
    try {
      const { data: s } = await api.post("/crypto/onramp/session", { product, asset });
      setWidgetUrl(s.widget_url);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "TRANSAK_SETUP_REQUIRED") setPendingNotice(true);
      else toast.error(errMsg(e));
    } finally {
      setLaunching(false);
    }
  };

  if (loading || !data) return <FullLoader />;

  const ready = onramp?.ready;
  const feats = [
    { i: TrendingUp, t: "Buy", product: "BUY" },
    { i: TrendingDown, t: "Sell", product: "SELL" },
    { i: ArrowUpRight, t: "Send", product: "BUY" },
    { i: ArrowDownLeft, t: "Receive", product: "SELL" },
  ];

  const hasHoldings = data.wallets && data.wallets.length > 0;
  const showTotal = hasHoldings && data.prices_available;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Crypto" subtitle={ready ? "Buy & sell crypto via Transak" : "Regulated crypto, when connected"} />

      <div className="rounded-3xl p-6 text-white mb-6 shadow-[0_18px_44px_rgba(75,35,204,0.3)]"
        style={{ background: "linear-gradient(135deg,#3A1A9F,#1B1340)" }} data-testid="crypto-portfolio-card">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-11 h-11 rounded-2xl bg-white/15"><Coins size={22} /></span>
          <div>
            <div className="font-display text-base font-semibold">Crypto portfolio</div>
            <div className="text-white/60 text-xs">Buy, sell, send &amp; receive</div>
          </div>
          {ready && <span className="ml-auto text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-white/15">{onramp.environment === "PRODUCTION" ? "Live" : "Sandbox"}</span>}
        </div>
        <div className="mt-6">
          <div className="text-white/55 text-xs uppercase tracking-[0.14em]">Total balance</div>
          <div className="font-display text-4xl font-semibold tabular-nums mt-1" data-testid="crypto-total-usd">
            {showTotal
              ? `$${Number(data.total_usd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : "$0.00"}
          </div>
          {hasHoldings && (
            <div className="flex items-center gap-1.5 mt-2 -ml-0.5">
              {data.wallets.slice(0, 6).map((w) => (
                <CoinIcon key={w.asset} asset={w.asset} size={22} />
              ))}
              <span className="text-white/55 text-xs ml-1">{data.wallets.length} asset{data.wallets.length > 1 ? "s" : ""}</span>
            </div>
          )}
        </div>
      </div>

      {hasHoldings && <PortfolioChart />}

      {!ready && (
        <ProviderNotice name="Crypto" label={data.status_label}
          message="Connect an approved crypto infrastructure provider (Transak) before enabling production transactions. We never create fake blockchain transactions, wallet addresses, or balances." />
      )}

      {ready && onramp.kyc_required && !onramp.kyc_verified && (
        <button data-testid="crypto-kyc-cta" onClick={() => nav("/app/kyc")}
          className="w-full flex items-center gap-3 p-4 rounded-2xl bg-warning/10 border border-warning/30 text-left mb-1">
          <ShieldCheck size={18} className="text-warning shrink-0" />
          <span className="text-sm font-medium text-warning">Verify your identity to buy or sell crypto</span>
        </button>
      )}

      <div className="grid grid-cols-4 gap-2 mt-5">
        {feats.map(({ i: Ic, t, product }) => (
          <button key={t} data-testid={`crypto-${t.toLowerCase()}`} onClick={() => openWidget(product)}
            className={`flex flex-col items-center gap-2 py-4 rounded-2xl bg-card border border-border transition-transform duration-200 active:scale-95 ${ready ? "hover:border-primary/40" : "opacity-70"}`}>
            <Ic size={20} className="text-primary" />
            <span className="text-[11px] font-medium">{t}</span>
          </button>
        ))}
      </div>

      {ready && (
        <p className="text-[11px] text-muted-foreground text-center mt-4">
          Crypto buy/sell is powered by Transak. Available worldwide to verified users.
        </p>
      )}

      {hasHoldings && (
        <div className="mt-6" data-testid="crypto-holdings">
          <div className="flex items-center justify-between mb-3 px-0.5">
            <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Your assets</h2>
            {data.prices_available && <span className="text-[11px] text-muted-foreground">Live prices</span>}
          </div>
          <div className="space-y-3">
            {data.wallets.map((w) => {
              const name = w.name || COIN[w.asset]?.name || w.asset;
              return (
                <div key={w.asset} data-testid={`holding-${w.asset}`} className="rounded-3xl bg-card border border-border p-5">
                  <div className="flex items-center gap-3">
                    <CoinIcon asset={w.asset} size={44} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{name}</div>
                      {w.usd_price != null
                        ? <div className="flex items-center gap-2 text-[11px] tabular-nums mt-0.5">
                            <span className="text-muted-foreground">${Number(w.usd_price).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                            {w.change_24h != null && (
                              <span data-testid={`holding-${w.asset}-change`}
                                className={`inline-flex items-center gap-0.5 font-semibold ${w.change_24h >= 0 ? "text-success" : "text-destructive"}`}>
                                {w.change_24h >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                                {w.change_24h >= 0 ? "+" : ""}{Number(w.change_24h).toFixed(2)}%
                              </span>
                            )}
                          </div>
                        : null}
                    </div>
                    <div className="text-right">
                      {w.usd_value != null && (
                        <div className="text-base font-semibold tabular-nums" data-testid={`holding-${w.asset}-value`}>${Number(w.usd_value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      )}
                      <div className="text-[11px] text-muted-foreground tabular-nums">{Number(w.amount).toLocaleString(undefined, { maximumFractionDigits: 8 })} {w.asset}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-border">
                    <button data-testid={`holding-${w.asset}-buy`} onClick={() => openWidget("BUY", w.asset)}
                      className={`flex items-center justify-center gap-1.5 h-10 rounded-xl bg-success/10 text-success text-xs font-semibold transition-transform duration-200 active:scale-95 ${ready ? "" : "opacity-60"}`}>
                      <TrendingUp size={14} /> Buy
                    </button>
                    <button data-testid={`holding-${w.asset}-sell`} onClick={() => openWidget("SELL", w.asset)}
                      className={`flex items-center justify-center gap-1.5 h-10 rounded-xl bg-destructive/10 text-destructive text-xs font-semibold transition-transform duration-200 active:scale-95 ${ready ? "" : "opacity-60"}`}>
                      <TrendingDown size={14} /> Sell
                    </button>
                    <button data-testid={`holding-${w.asset}-send`} onClick={() => openWidget("SELL", w.asset)}
                      className={`flex items-center justify-center gap-1.5 h-10 rounded-xl bg-primary/10 text-primary text-xs font-semibold transition-transform duration-200 active:scale-95 ${ready ? "" : "opacity-60"}`}>
                      <ArrowUpRight size={14} /> Send
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {markets?.coins?.length > 0 && (
        <div className="mt-6" data-testid="crypto-markets">
          <div className="flex items-center justify-between mb-3 px-0.5">
            <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">All coins</h2>
            {markets.prices_available && <span className="text-[11px] text-muted-foreground">Live prices</span>}
          </div>
          <div className="space-y-2">
            {markets.coins.map((c) => (
              <div key={c.asset} data-testid={`market-${c.asset}`} className="flex items-center gap-3 rounded-2xl bg-card border border-border p-3.5">
                <CoinIcon asset={c.asset} size={38} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{c.name}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{c.asset}</div>
                </div>
                <div className="text-right">
                  {c.usd_price != null ? (
                    <>
                      <div className="text-sm font-semibold tabular-nums">${Number(c.usd_price).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                      {c.change_24h != null && (
                        <div className={`text-[11px] font-semibold inline-flex items-center gap-0.5 ${c.change_24h >= 0 ? "text-success" : "text-destructive"}`}>
                          {c.change_24h >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                          {c.change_24h >= 0 ? "+" : ""}{Number(c.change_24h).toFixed(2)}%
                        </div>
                      )}
                    </>
                  ) : <div className="text-[11px] text-muted-foreground">—</div>}
                </div>
                <button data-testid={`market-${c.asset}-buy`} onClick={() => openWidget("BUY", c.asset)}
                  className={`ml-1 h-9 px-3.5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold transition-transform duration-200 active:scale-95 ${ready ? "" : "opacity-60"}`}>
                  Buy
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <button data-testid="crypto-bridge-link" onClick={() => nav("/app/bridge")}
        className="mt-4 w-full flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/15 transition-transform duration-200 active:scale-[0.98]">
        <span className="grid place-items-center w-10 h-10 rounded-xl bg-primary text-primary-foreground shrink-0"><Coins size={18} /></span>
        <span className="flex-1 text-left">
          <span className="block text-sm font-semibold">Stablecoin on/off-ramp</span>
          <span className="block text-[11px] text-muted-foreground">Fiat ↔ USDC/USDT via Bridge</span>
        </span>
        <ArrowUpRight size={18} className="text-primary" />
      </button>

      {/* Transak pending-approval notice — a clean, friendly state instead of a raw error */}
      {pendingNotice && createPortal((
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          data-testid="transak-pending-modal" onClick={() => setPendingNotice(false)}>
          <div className="w-full max-w-sm rounded-3xl bg-card border border-border p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-center">
              <span className="grid place-items-center w-16 h-16 rounded-full bg-warning/10 text-warning">
                <Clock size={30} />
              </span>
            </div>
            <h3 className="font-display text-lg font-semibold text-center mt-4">Crypto is almost ready</h3>
            <p className="text-sm text-muted-foreground text-center mt-2 leading-relaxed">
              Your Transak account is still being approved. Buy &amp; sell will switch on automatically once Transak
              verifies your business and your live domain is whitelisted. Your crypto prices below are live in the meantime.
            </p>
            <div className="rounded-2xl bg-muted/40 border border-border p-3 mt-4 text-[12px] text-muted-foreground space-y-1.5">
              <div className="flex gap-2"><span className="text-primary font-bold">1.</span> Finish your Transak KYB (business verification).</div>
              <div className="flex gap-2"><span className="text-primary font-bold">2.</span> Add your live domain &amp; server IP in the Transak dashboard.</div>
              <div className="flex gap-2"><span className="text-primary font-bold">3.</span> Buy/Sell turns on here with no extra steps.</div>
            </div>
            <button data-testid="transak-pending-close" onClick={() => setPendingNotice(false)}
              className="w-full h-12 mt-5 rounded-2xl bg-primary text-primary-foreground font-semibold transition-transform active:scale-95">
              Got it
            </button>
          </div>
        </div>
      ), document.body)}

      {/* In-app Transak widget (opens when a session is successfully minted) */}
      {widgetUrl && createPortal((
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
          data-testid="transak-widget-modal">
          <div className="relative w-full h-full sm:h-[85vh] sm:max-w-md bg-white sm:rounded-3xl overflow-hidden shadow-2xl">
            <button data-testid="transak-widget-close" onClick={() => setWidgetUrl(null)}
              className="absolute top-3 right-3 z-10 grid place-items-center w-9 h-9 rounded-full bg-black/60 text-white">
              <X size={18} />
            </button>
            <iframe title="Transak" src={widgetUrl} allow="camera;microphone;payment;clipboard-write"
              className="w-full h-full border-0" />
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
